import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  getSharedDCCTVolumeInfo,
  type FullHeadMPRProjection,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';
import {
  MedicalImageZoomLayout,
  RadioGroup,
  Switch,
  Tooltip,
} from '@/shared/ui';

import { medicalImageTitles } from '@/entities/assets';
import { reportsModel } from '@/entities/reports';

import type { FullHeadMprReportSelectionResolver } from '@/features/MPR';

import {
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  TOOTH_BASIS_SERIES_TO_GROUP,
  type FullHeadMprSliceGroupView,
  type FullHeadMprSliceTileView,
  type ToothSliceGeometry,
} from '../../config';
import { useToothSliceSettings } from '../../hooks';
import { resolveToothSliceThicknessOptionsMm } from '../../lib';
import { FullHeadMprSliceOptionsControls } from '../FullHeadMprSliceOptionsControls';
import { FullHeadMprSliceSession } from '../FullHeadMprSliceSession';

import styles from './FullHeadMprSliceZoomModal.module.scss';

type FullHeadMprSliceZoomModalProps = {
  group: FullHeadMprSliceGroupView | null;
  input: ToothSliceGeometry | null;
  tile: FullHeadMprSliceTileView | null;
  className?: string;
  groups?: FullHeadMprSliceGroupView[];
  onClose: () => void;
  resolveViewSelection: FullHeadMprReportSelectionResolver;
};

export const FullHeadMprSliceZoomModal: FC<FullHeadMprSliceZoomModalProps> = (
  props,
) => {
  const {
    group,
    groups,
    input,
    tile,
    className,
    onClose,
    resolveViewSelection,
  } = props;

  const { formatMessage } = useIntl();

  const [mode, setMode] = useState<'single' | 'three'>('single');
  const [isOnlyThisSlice, setIsOnlyThisSlice] = useState(false);
  const [primaryProjection, setPrimaryProjection] =
    useState<FullHeadMPRProjection | null>(null);
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  useEffect(() => {
    setMode('single');
    setIsOnlyThisSlice(false);
    setPrimaryProjection(null);
  }, [tile?.id]);

  const thicknessOptionsMm = resolveToothSliceThicknessOptionsMm(
    input ? getSharedDCCTVolumeInfo(input.volumeFileID) : null,
  );
  const toothID = tile?.kind === 'view' ? tile.toothID : input?.toothID ?? '';

  const { canChangeOptions, recordsByTileID, sliceOptions, setSeriesOptions } =
    useToothSliceSettings({
      reportID: report?.ID ?? '',
      thicknessOptionsMm,
      toothID,
    });

  if (!tile || (tile.kind !== 'view' && !group)) {
    return null;
  }

  const controlledSeriesName = primaryProjection
    ? FULL_HEAD_MPR_PROJECTION_TO_SERIES[primaryProjection]
    : tile.seriesName;
  const options = sliceOptions[controlledSeriesName];
  const title = formatMessage(
    medicalImageTitles[TOOTH_BASIS_SERIES_TO_GROUP[controlledSeriesName]],
  );

  return (
    <FullHeadMprSliceSession
      className={styles.mprSession}
      group={group}
      groups={groups}
      input={input}
      isSingleProjection={mode === 'single'}
      isThicknessOverrideEnabled={isOnlyThisSlice}
      recordsByTileID={recordsByTileID}
      resolveViewSelection={resolveViewSelection}
      sliceOptions={sliceOptions}
      tile={tile}
      toothID={toothID}
      onPrimaryProjectionChange={setPrimaryProjection}
      renderLayout={({
        children,
        controls,
        shortcutsControl,
        sliceThickness,
        ...layoutProps
      }) => (
        <MedicalImageZoomLayout
          {...layoutProps}
          isOpen
          className={cn(styles.modalContainer, className)}
          imageContainerClassName={styles.imageContainer}
          strideMm={tile.kind === 'view' ? 0 : options.sliceStepMm}
          thicknessControl={
            tile.kind === 'series' && (
              <div className={styles.thicknessControls}>
                <FullHeadMprSliceOptionsControls
                  key={`${controlledSeriesName}:${isOnlyThisSlice}`}
                  disabled={
                    !canChangeOptions ||
                    !primaryProjection ||
                    (isOnlyThisSlice && sliceThickness.isSaving)
                  }
                  options={
                    isOnlyThisSlice && sliceThickness.overrideMm !== null
                      ? { ...options, thicknessMm: sliceThickness.overrideMm }
                      : options
                  }
                  showStep={false}
                  thicknessOptionsMm={thicknessOptionsMm}
                  onChange={(nextOptions) => {
                    if (isOnlyThisSlice) {
                      sliceThickness.setOverrideMm(nextOptions.thicknessMm);
                      sliceThickness.saveAsView();
                      return;
                    }
                    setSeriesOptions(controlledSeriesName, nextOptions);
                  }}
                />
                <Tooltip.Primary
                  content={formatMessage({
                    id: 'fullHeadMpr.onlyThisSlice.hint',
                    defaultMessage:
                      'Changes the thickness of this slice only and saves it as a separate slice; the series thickness stays unchanged.',
                  })}
                >
                  <Switch
                    className={styles.onlyThisSlice}
                    checked={isOnlyThisSlice}
                    disabled={!canChangeOptions || !primaryProjection}
                    onChange={(event) =>
                      setIsOnlyThisSlice(event.target.checked)
                    }
                  >
                    {formatMessage({
                      id: 'fullHeadMpr.onlyThisSlice',
                      defaultMessage: 'Only this slice',
                    })}
                  </Switch>
                </Tooltip.Primary>
              </div>
            )
          }
          thicknessMm={
            tile.kind === 'view' ? tile.view.thicknessMm : options.thicknessMm
          }
          title={title}
          onClose={onClose}
          controls={
            <div className={styles.controls}>
              <RadioGroup
                variant="tab"
                name="fullHeadMprSliceMode"
                value={mode}
                items={[
                  {
                    value: 'single',
                    label: (
                      <FormattedMessage
                        id="global.slice"
                        defaultMessage="Slice"
                      />
                    ),
                  },
                  {
                    value: 'three',
                    label: (
                      <FormattedMessage
                        id="medicalImageTitle.savedMPR"
                        defaultMessage="MPR"
                      />
                    ),
                  },
                ]}
                onChange={({ target: { value } }) => {
                  if (value === 'single' || value === 'three') {
                    setMode(value);
                  }
                }}
              />
              {controls && (
                <div className={styles.viewControls}>{controls}</div>
              )}
              {shortcutsControl}
            </div>
          }
        >
          {children}
        </MedicalImageZoomLayout>
      )}
    />
  );
};
